import qs from "qs";
import ajax from "Utils/ajax";
import { Dispatch } from "react";
import {
  setYouVisitAnalyticsConfig,
  setPersonalizationEnabled,
  setExperienceOffline,
  setExperienceNotFound,
} from "HOC/YouVisitAnalytics/store/actions";
import { onetrust } from "../../shared/analyticsManager";
import { btoaWithCatch } from "./analyticUtils";
import Socket from "../socket/socket";
import EventAnalytics from "../EventAnalytics/analytics";

interface AnalyticsData {
  queuedStop: any;
  userkey: string;
  sessionId: string;
  module: string;
  moduleId: string;
  socketUrl: string;
  socket: Socket;
  instId: string | number;
  locId: string | number;
  tourId: string;
  platform: string;
  createAnalyticsModuleParams: any;
  hasInitialized: boolean;
  analyticsTime: string;
}

interface AnalyticsObject {
  userkey: any;
  socket: Socket;
  analyticsUrl: string;
}

interface AnalyticsData {
  analytics: AnalyticsObject;
  // eslint-disable-next-line
  personalization_module_enabled: boolean;
}

interface AnalyticsModuleResponse {
  status?: number;
  data: AnalyticsData;
  error?: string;
}

interface LoadConfiguration {
  analytics?: AnalyticsObject;
  socket?: Socket;
  analyticsUrl?: string;
  userkey?: string;
  sessionId?: string;
  module?: string;
  analyticsTime?: string | number;
  platform?: string;
  stops?: any;
  location?: any;
  disabled: boolean;
}

class Analytics {
  static analyticsData: AnalyticsData = {
    userkey: null,
    sessionId: null,
    module: null,
    moduleId: null,
    socketUrl: null,
    socket: null,
    instId: null,
    locId: null,
    tourId: null,
    createAnalyticsModuleParams: {},
    hasInitialized: false,
    platform: "",
    analytics: null,
    analyticsTime: null,
    queuedStop: null,
    personalization_module_enabled: false,
  };

  static init(
    instId: string | number,
    locId: string | number,
    tourId: string,
    platform: string,
    dispatch?: Dispatch<any>
  ) {
    // Determine if a socket connection already exists. If so close it.
    Analytics.closeSocket();
    Analytics.analyticsData.instId = instId;
    Analytics.analyticsData.locId = locId;
    Analytics.analyticsData.tourId = tourId;
    Analytics.analyticsData.platform = platform;

    return Analytics.getAnalyticsModule(instId, locId, platform).then(
      (response: AnalyticsModuleResponse) => {
        const { data } = response;
        if (data && data.analytics) {
          const loadConfig: LoadConfiguration = {
            ...{ disabled: false },
            ...data.analytics,
            analyticsUrl: process.env.REACT_APP_ANALYTICS_WEBSOCKET_ENDPOINT,
          };
          if (dispatch) {
            dispatch(setYouVisitAnalyticsConfig({ ...data, isReady: true }));
            dispatch(
              setPersonalizationEnabled(data.personalization_module_enabled)
            );
          }
          Analytics.load(loadConfig);

          return loadConfig;
        }
        if (response.status && response.status === 404) {
          if (
            dispatch &&
            response.error?.includes("<title>Experience Offline!</title>")
          ) {
            dispatch(setExperienceOffline(true));
          } else {
            window.location.href = `${window.location.origin}/404`;
          }
        } else if (
          response.error === undefined &&
          response.status === undefined
        ) {
          dispatch(setExperienceNotFound(true));
        }
        // in development environments, there seems to be a problem with analytics data
        // eslint-disable-next-line no-console
        console.error(
          "Error loading analytics data. Possibly pointing to wrong database"
        );
        return false;
      }
    );
  }

  static load(config: LoadConfiguration) {
    const {
      userkey,
      sessionId,
      module,
      analyticsUrl,
      analyticsTime,
      socket,
      platform,
      disabled,
    } = config;
    Analytics.analyticsData = Object.assign(Analytics.analyticsData, {
      platform,
      userkey,
      sessionId,
      module,
      socketUrl: analyticsUrl,
      analyticsTime,
      socket,
    });
    if (!disabled) {
      setTimeout(Analytics.establishLink.bind(Analytics), 1000);
    } else {
      // todo
    }
  }

  static establishLink() {
    const { socketUrl, socket } = Analytics.analyticsData;
    const analyticsSocket =
      socket || new Socket(socketUrl, [], { automaticOpen: false });
    Analytics.analyticsData.socket = analyticsSocket;

    // Update event analytics with new socket
    EventAnalytics.setSocket(Analytics.analyticsData.socket);

    analyticsSocket.open();

    analyticsSocket.onmessage = (event: { data: string }) => {
      const { action } = JSON.parse(event.data);

      switch (action) {
        case "analyticsInitialized":
          Analytics.analyticsInitialized();
          break;
        default:
      }
    };

    Analytics.initAnalytics();
    return analyticsSocket;
  }

  static analyticsInitialized() {
    Analytics.analyticsData.hasInitialized = true;

    // // If they're queuedStops process them
    // if (Analytics.analyticsData.queuedStop !== null) {
    //   Analytics.stopEntered(Analytics.analyticsData.queuedStop);
    //   Analytics.analyticsData.queuedStop = null;
    // }
  }

  static getAnalyticsAttributesURL(
    instId: string | number,
    locId: string | number,
    platform: string
  ): string {
    const baseUrl = process.env.REACT_APP_ANALYTICS_SERVER_URL;
    const sessionType = process.env.REACT_APP_ANALYTICS_SESSSION_TYPE;
    const partnerPath =
      locId && locId !== "0" ? `${instId}/${locId}` : `${instId}`;
    return `${baseUrl}map/getAnalyticsAndRegistrationAttributes/${partnerPath}${window.location.search}?session_type=${sessionType}&pl=${platform}`;
  }

  static getAnalyticsModule(
    instId: string | number,
    locId: string | number,
    platform: string
  ) {
    return ajax.get(
      Analytics.getAnalyticsAttributesURL(instId, locId, platform)
    );
  }

  static sendAnalytics(action: any): any {
    if (!onetrust.hasAnalyticsCookiesConsent()) {
      return false;
    }

    const {
      userkey,
      sessionId,
      module,
      instId,
      locId,
    } = Analytics.analyticsData;

    if (!(userkey && sessionId && module)) return false;

    const estid = /estid=(\w+)/g.exec(window.location.search);
    const params = estid && estid.length === 2 ? `estid=${estid[1]}` : "";

    const url = `${process.env.REACT_APP_TRACKING_PIXEL_URL}/cta/${instId}/${locId}.png?${params}`;
    document.body.insertAdjacentHTML(
      "beforeend",
      `<img alt="youvisit action pixel" src="${url}" style="display:none;" />`
    );

    const queryStr = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    });

    return ajax.get(
      `${process.env.REACT_APP_ANALYTICS_SERVER_URL}tour/Tour/registerAction`,
      {
        ...queryStr,
        userkey,
        sessionid: sessionId,
        module: "fullmap",
        moduleId: "fullmap",
        action,
      }
    );
  }

  static stopEntered(stopid: string) {
    if (!onetrust.hasAnalyticsCookiesConsent()) {
      return;
    }

    const { socket, sessionId, hasInitialized } = Analytics.analyticsData;

    // Socket is not ready yet so add it stop to queue
    if (socket == null || !hasInitialized) {
      Analytics.analyticsData.queuedStop = stopid;
    } else {
      socket.sendMessage(
        "stopEntered",
        btoaWithCatch(JSON.stringify({ stopid, sessionId }))
      );
    }
  }

  static sessionIdChanged(sessionId: string) {
    Analytics.analyticsData.sessionId = sessionId;
  }

  static closeSocket() {
    const { socket } = Analytics.analyticsData;

    if (socket) socket.close();
  }

  // eslint-disable-next-line no-underscore-dangle
  static _sessionIdChanged(_event: any, data: any) {
    Analytics.sessionIdChanged(data.sessionId);
  }

  static initAnalytics(isReinitalization = false) {
    if (!onetrust.hasAnalyticsCookiesConsent()) {
      return;
    }

    const {
      userkey,
      sessionId,
      analyticsTime,
      queuedStop,
      module,
      createAnalyticsModuleParams,
      instId,
      locId,
      platform,
    } = Analytics.analyticsData;

    const isReversedOrientation =
      window.orientation !== undefined &&
      (window.orientation === -90 || window.orientation === 90);

    const width = isReversedOrientation
      ? window.screen.width
      : window.screen.height;
    const height = isReversedOrientation
      ? window.screen.height
      : window.screen.width;
    let currentURL;

    // Use try catch to prevent smartscript from halting in localhost
    try {
      if (
        window.parent &&
        window.parent.location.href &&
        // eslint-disable-next-line eqeqeq
        window.location.href != window.parent.location.href
      )
        currentURL = window.parent && window.parent.location.href;
      else currentURL = document.referrer || window.location.href;
    } catch (e) {
      currentURL = document.referrer || window.location.href;
    }

    // If there is a stop in queue then send a message to node server and clear the queue
    if (queuedStop !== null) {
      Analytics.analyticsData.queuedStop = null;
    }

    // Forward query string parameters to backend when creating analyics
    const queryStr = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    });

    const initAnalytics = {
      endpoints: {
        ANALYTICS: `${process.env.REACT_APP_ANALYTICS_SERVER_URL}functions.php`,
        GPS: `${process.env.REACT_APP_ANALYTICS_SERVER_URL}tour/analytics/gpsBatchUpload`,
        NEWSESSION: Analytics.getAnalyticsAttributesURL(
          instId,
          locId,
          platform
        ),
      },
      queuedStop,
      closeAnalyticsModule: {
        method: "closeAnalyticsModule",
        userkey,
        session: sessionId,
        time: analyticsTime,
      },
      createAnalyticsModule: {
        ...queryStr,
        method: "createAnalyticsModule",
        userkey,
        session: sessionId,
        module,
        time: analyticsTime,
        screenWidth: width,
        screenHeight: height,
        referrer: escape(document.referrer),
        currentURL,
        ...createAnalyticsModuleParams,
      },
      isReinitalization,
    };
    Analytics.analyticsData.socket.sendMessage(
      "initAnalytics",
      btoaWithCatch(JSON.stringify(initAnalytics))
    );
  }
}

export default Analytics;
